import { useState } from 'react';
import { GripVertical, Trash2, Pencil } from 'lucide-react';
import type { WidgetConfig, ExecutionDataRow, MetricDefinition } from '@monitoring-panel/types';
import { TableWidget } from './widgets/TableWidget';
import { ChartWidget } from './widgets/ChartWidget';
import { WidgetConfigModal } from './WidgetConfigModal';
import { cn } from '@/lib/utils';

interface WidgetProps {
  config: WidgetConfig;
  campaignData: ExecutionDataRow[];
  availableColumns: string[];
  availableMetrics: MetricDefinition[];
  onUpdate: (updates: Partial<WidgetConfig>) => void;
  onDelete: () => void;
}

export function Widget({
  config,
  campaignData,
  availableColumns,
  availableMetrics,
  onUpdate,
  onDelete,
}: WidgetProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete();
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  const handleSaveEdit = (updatedWidget: WidgetConfig) => {
    // Pass the full updated config to onUpdate
    onUpdate(updatedWidget);
    setIsEditModalOpen(false);
  };

  return (
    <div className="h-full adv-card flex flex-col overflow-hidden">
      {/* Widget Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-adv-border bg-adv-inset">
        <div className="flex items-center gap-2">
          <div className="widget-drag-handle cursor-move p-1 -ml-1 rounded-adv-sm hover:bg-adv-sunken">
            <GripVertical size={16} className="text-adv-tertiary" />
          </div>
          <h3 className="text-adv-body-m font-bold text-adv-primary truncate">{config.title}</h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm"
            title="Edit widget"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={handleDelete}
            className={cn(
              'adv-btn adv-btn--icon adv-btn--sm',
              showDeleteConfirm ? 'adv-btn--danger bg-adv-warning-bg' : 'adv-btn--ghost'
            )}
            title={showDeleteConfirm ? 'Click again to confirm' : 'Delete widget'}
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Widget Content */}
      <div className="flex-1 overflow-auto p-3">
        {config.type === 'table' ? (
          <TableWidget config={config} campaignData={campaignData} availableMetrics={availableMetrics} />
        ) : (
          <ChartWidget config={config} campaignData={campaignData} availableMetrics={availableMetrics} />
        )}
      </div>

      {/* Edit Modal */}
      <WidgetConfigModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        availableColumns={availableColumns}
        campaignData={campaignData}
        availableMetrics={availableMetrics}
        defaultType={config.type}
        onCreate={() => {}} // Not used in edit mode
        editingWidget={config}
        onSave={handleSaveEdit}
      />
    </div>
  );
}
